
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import { StudentForm } from "./StudentForm";
import { StudentDetails } from "./StudentDetails";
import { Download, Edit, Plus, Search, Trash, User, X, ChevronDown, Loader2 } from "lucide-react";
import * as XLSX from 'xlsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";

// Type for medical alerts array
type MedicalAlert = {
  type: string;
  description: string;
};

// Type for student with proper medical_alerts typing
type Student = {
  id: string;
  surname: string;
  other_names: string | null;
  class: string;
  stream: string | null;
  medical_alerts: MedicalAlert[];
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  school_id: string;
  created_at: string;
  updated_at: string;
  tutor1_name: string | null;
  tutor1_contact: string | null;
  tutor2_name: string | null;
  tutor2_contact: string | null;
};

export function StudentList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [classFilter, setClassFilter] = useState<string>("all");
  const [streamFilter, setStreamFilter] = useState<string>("all");
  const [alertFilter, setAlertFilter] = useState<boolean | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [viewingStudent, setViewingStudent] = useState<Student | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch students
  const { data: students, refetch } = useQuery({
    queryKey: ['students'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .order('surname');
      
      if (error) throw error;

      // Parse medical_alerts JSON into proper type
      return (data as any[]).map(student => ({
        ...student,
        medical_alerts: Array.isArray(student.medical_alerts) 
          ? student.medical_alerts 
          : []
      })) as Student[];
    },
    enabled: !!user?.schoolId,
  });

  // Filter students based on search and filters
  const filteredStudents = students?.filter(student => {
    const matchesSearch = searchTerm === "" || 
      `${student.surname} ${student.other_names || ""}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClass = classFilter === "all" || student.class === classFilter;
    const matchesStream = streamFilter === "all" || student.stream === streamFilter;
    const matchesAlert = alertFilter === null || 
      (alertFilter ? (student.medical_alerts.length > 0) : (student.medical_alerts.length === 0));
    
    return matchesSearch && matchesClass && matchesStream && matchesAlert;
  });

  // Get unique classes and streams for filters
  const classes = [...new Set(students?.map(s => s.class))].sort();
  const streams = [...new Set(students?.map(s => s.stream).filter(Boolean))].sort();

  const handleDelete = async (studentId: string) => {
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('students')
        .delete()
        .eq('id', studentId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Student deleted successfully",
      });

      await queryClient.invalidateQueries({ queryKey: ['students'] });
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error in deletion process:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete student. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('students')
        .delete()
        .in('id', Array.from(selectedStudents));

      if (error) throw error;

      toast({
        title: "Success",
        description: `Successfully deleted ${selectedStudents.length} students`,
      });

      setSelectedStudents([]);
      await queryClient.invalidateQueries({ queryKey: ['students'] });
    } catch (error) {
      console.error('Error in bulk deletion process:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete selected students. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const exportToExcel = () => {
    if (!filteredStudents?.length) return;

    // Get the list of students to export based on selection
    const studentsToExport = selectedStudents.length > 0
      ? filteredStudents.filter(student => selectedStudents.includes(student.id))
      : filteredStudents;

    const exportData = studentsToExport.map(student => ({
      'Surname': student.surname,
      'Other Names': student.other_names || '',
      'Class': student.class,
      'Stream': student.stream || '',
      'Medical Alerts': student.medical_alerts?.length || 0,
      'Emergency Contact': student.emergency_contact_name || '',
      'Emergency Phone': student.emergency_contact_phone || '',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Students");
    XLSX.writeFile(wb, "students.xlsx");
  };

  const hasActiveFilters = searchTerm !== "" || 
    classFilter !== "all" || 
    streamFilter !== "all" || 
    alertFilter !== null;

  const clearFilters = () => {
    setSearchTerm("");
    setClassFilter("all");
    setStreamFilter("all");
    setAlertFilter(null);
  };

  const handleRowClick = (student: Student) => {
    setViewingStudent(student);
  };

  const handleCheckboxClick = (
    e: React.MouseEvent<HTMLInputElement>,
    studentId: string
  ) => {
    // Stop propagation to prevent row click handler from firing
    e.stopPropagation();
    
    if (selectedStudents.includes(studentId)) {
      setSelectedStudents(selectedStudents.filter(id => id !== studentId));
    } else {
      setSelectedStudents([...selectedStudents, studentId]);
    }
  };

  const handleCellClick = (
    e: React.MouseEvent<HTMLTableCellElement>,
    action: () => void
  ) => {
    // Stop propagation to prevent row click handler from firing
    e.stopPropagation();
    action();
  };

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Student List</CardTitle>
              <CardDescription>
                View and manage student records
              </CardDescription>
            </div>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm">
                <ChevronDown className={`h-4 w-4 transform ${isOpen ? 'rotate-180' : ''} transition-transform`} />
                <span className="sr-only">Toggle student list</span>
              </Button>
            </CollapsibleTrigger>
          </div>
        </CardHeader>
        <CardContent>
          <CollapsibleContent className="space-y-4">
            {/* Search and Filters */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={classFilter} onValueChange={setClassFilter}>
                  <SelectTrigger className="w-[100px]">
                    <SelectValue placeholder="Class" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Classes</SelectItem>
                    {classes.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={streamFilter} onValueChange={setStreamFilter}>
                  <SelectTrigger className="w-[100px]">
                    <SelectValue placeholder="Stream" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Streams</SelectItem>
                    {streams.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select 
                  value={alertFilter === null ? "all" : alertFilter.toString()} 
                  onValueChange={(v) => setAlertFilter(v === "all" ? null : v === "true")}
                >
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Medical Alerts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Students</SelectItem>
                    <SelectItem value="true">With Alerts</SelectItem>
                    <SelectItem value="false">No Alerts</SelectItem>
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={clearFilters}
                    className="h-10"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setShowAddForm(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Student
              </Button>
              <Button variant="outline" onClick={exportToExcel}>
                <Download className="mr-2 h-4 w-4" />
                Export to Excel
              </Button>
              {selectedStudents.length > 0 && (
                <Button 
                  variant="destructive" 
                  onClick={() => setDeleteConfirm('bulk')}
                  disabled={isDeleting}
                >
                  <Trash className="mr-2 h-4 w-4" />
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    `Delete Selected (${selectedStudents.length})`
                  )}
                </Button>
              )}
            </div>

            {/* Students Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedStudents.length === filteredStudents?.length && filteredStudents?.length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedStudents(filteredStudents?.map(s => s.id) || []);
                          } else {
                            setSelectedStudents([]);
                          }
                        }}
                        aria-label="Select all students"
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Stream</TableHead>
                    <TableHead>Medical Alerts</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStudents?.map((student) => (
                    <TableRow 
                      key={student.id} 
                      onClick={() => handleRowClick(student)}
                      className="cursor-pointer hover:bg-muted/50"
                    >
                      <TableCell 
                        onClick={(e) => e.stopPropagation()}
                        className="p-0 pl-4"
                      >
                        <Checkbox
                          checked={selectedStudents.includes(student.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedStudents([...selectedStudents, student.id]);
                            } else {
                              setSelectedStudents(selectedStudents.filter(id => id !== student.id));
                            }
                          }}
                          aria-label={`Select ${student.surname}`}
                        />
                      </TableCell>
                      <TableCell 
                        className="cursor-pointer font-medium hover:underline"
                        onClick={(e) => handleCellClick(e, () => setViewingStudent(student))}
                      >
                        {student.surname}
                        {student.other_names && `, ${student.other_names}`}
                      </TableCell>
                      <TableCell 
                        className="cursor-pointer hover:bg-muted" 
                        onClick={(e) => handleCellClick(e, () => setEditingStudent(student))}
                      >
                        {student.class}
                      </TableCell>
                      <TableCell 
                        className="cursor-pointer hover:bg-muted"
                        onClick={(e) => handleCellClick(e, () => setEditingStudent(student))}
                      >
                        {student.stream || '-'}
                      </TableCell>
                      <TableCell 
                        className="cursor-pointer hover:bg-muted"
                        onClick={(e) => handleCellClick(e, () => setViewingStudent(student))}
                      >
                        <span className={`${student.medical_alerts.length > 0 ? 'font-semibold text-amber-600' : ''}`}>
                          {student.medical_alerts.length || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-right p-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => setViewingStudent(student)}
                          >
                            <User className="h-4 w-4" />
                            <span className="sr-only">View student</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => setEditingStudent(student)}
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit student</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => setDeleteConfirm(student.id)}
                          >
                            <Trash className="h-4 w-4" />
                            <span className="sr-only">Delete student</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CollapsibleContent>

          {/* Add/Edit Form Dialog */}
          {(showAddForm || editingStudent) && (
            <StudentForm
              student={editingStudent}
              onClose={() => {
                setShowAddForm(false);
                setEditingStudent(null);
                refetch();
              }}
            />
          )}

          {/* View Student Details */}
          {viewingStudent && (
            <StudentDetails
              student={viewingStudent}
              onBack={() => setViewingStudent(null)}
            />
          )}

          {/* Delete Confirmation */}
          <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  {deleteConfirm === 'bulk'
                    ? `This will permanently delete ${selectedStudents.length} students and all their related records (visits, referrals, etc.).`
                    : 'This will permanently delete this student and all their related records (visits, referrals, etc.).'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    if (deleteConfirm === 'bulk') {
                      handleBulkDelete();
                    } else {
                      handleDelete(deleteConfirm);
                    }
                  }}
                  disabled={isDeleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Collapsible>
    </Card>
  );
}
